import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdLayout from '../components/AdLayout';
import DataTable from '../components/ui/DataTable';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL_All } from "../api";
import axiosInstance from "../axiosInstance";
import { fetchWebsiteNewsPermissions } from "../libs/customerCreatePermission";
import '../css/newsList.css';

const NewsList = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [newsPerm, setNewsPerm] = useState({
    loaded: false,
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
  });

  const categories = [
    { value: "all", label: "Tất cả" },
    { value: "tin-tuc-thi-truong", label: "Tin tức thị trường" },
    { value: "kien-thuc-xnk", label: "Kiến thức XNK" },
    { value: "nguon-hang", label: "Nguồn hàng" }
  ];

  useEffect(() => {
    const loadPerm = async () => {
      const p = await fetchWebsiteNewsPermissions(axiosInstance);
      setNewsPerm({
        loaded: true,
        canView: Boolean(p?.canView),
        canCreate: Boolean(p?.canCreate),
        canEdit: Boolean(p?.canEdit),
        canDelete: Boolean(p?.canDelete),
      });
    };
    loadPerm();
  }, []);

  // Load articles
  useEffect(() => {
    const loadArticles = async () => {
      if (!newsPerm.loaded || !newsPerm.canView) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await axiosInstance.get(`${API_URL_All}/api/news`);
        setArticles(response.data);
        setFilteredArticles(response.data);
      } catch (error) {
        console.error('Error loading articles:', error);
        toast.error("Không thể tải danh sách bài viết!");
      } finally {
        setLoading(false);
      }
    };
    
    loadArticles();
  }, [newsPerm.loaded, newsPerm.canView]);

  // Filter articles
  useEffect(() => {
    let filtered = articles;

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(article => article.categorySlug === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredArticles(filtered);
  }, [searchTerm, selectedCategory, articles]);

  const handleDelete = async (id) => {
    if (!newsPerm.canDelete) {
      toast.error("Bạn không có quyền xóa bài viết!");
      return;
    }
    if (window.confirm("Bạn có chắc chắn muốn xóa bài viết này?")) {
      try {
        await axiosInstance.delete(`${API_URL_All}/api/news/${id}`);
        const updatedArticles = articles.filter(article => article.id !== id);
        setArticles(updatedArticles);
        toast.success("Xóa bài viết thành công!");
      } catch (error) {
        console.error('Error deleting article:', error);
        toast.error("Không thể xóa bài viết!");
      }
    }
  };

  if (loading) {
    return (
      <AdLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdLayout>
    );
  }

  return (
    <AdLayout>
      {newsPerm.loaded && !newsPerm.canView ? (
        <div className="container-fluid p-3 p-md-4">
          <div className="alert alert-warning mb-0">Bạn không có quyền truy cập màn hình này.</div>
        </div>
      ) : (
      <div className="news-list-container p-3 p-md-4">
        {/* Header */}
        <div className="bg-white p-4 rounded-3 shadow-sm mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h2 className="mb-1 text-dark fs-4 fw-semibold">Quản lý bài viết</h2>
              <p className="text-muted mb-0">Tổng số: {filteredArticles.length} bài viết</p>
            </div>
            {newsPerm.canCreate && (
              <Link 
                to="/admin/news/new" 
                className="btn btn-primary"
              >
                <i className="fas fa-plus me-2"></i>
                Tạo bài viết mới
              </Link>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-3 p-md-4 rounded-3 shadow-sm mb-3">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="search-box">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="form-control rounded-2"
                  placeholder="Tìm kiếm bài viết..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <select
                className="form-select rounded-2"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Articles Table */}
        <div className="bg-white p-3 p-md-4 rounded-3 shadow-sm">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <p className="text-muted">Không tìm thấy bài viết nào</p>
            </div>
          ) : (
            <DataTable maxHeight="calc(100vh - 340px)">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>ID</th>
                    <th style={{ width: '80px' }}>Ảnh</th>
                    <th>Tiêu đề</th>
                    <th style={{ width: '150px' }}>Danh mục</th>
                    <th style={{ width: '120px' }}>Ngày đăng</th>
                    <th style={{ width: '100px' }}>Tác giả</th>
                    <th style={{ width: '80px' }}>Nổi bật</th>
                    <th style={{ width: '150px' }} className="text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArticles.map((article) => (
                    <tr key={article.id}>
                      <td>{article.id}</td>
                      <td>
                        <img 
                          src={article.imageUrl || 'https://via.placeholder.com/60x40'} 
                          alt={article.title}
                          className="article-thumbnail rounded"
                        />
                      </td>
                      <td>
                        <div className="article-title-cell">
                          <div className="fw-semibold text-dark lh-sm mb-1">{article.title}</div>
                          <div className="text-muted small lh-sm">
                            {article.excerpt.substring(0, 80)}...
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge bg-zto px-3 py-2 rounded-pill fw-medium`}>
                          {article.category}
                        </span>
                      </td>
                      <td>
                        <small>{article.publishDate}</small>
                      </td>
                      <td>
                        <small>{article.author}</small>
                      </td>
                      <td className="text-center">
                        {article.featured ? (
                          <i className="fas fa-star text-warning"></i>
                        ) : (
                          <i className="far fa-star text-muted"></i>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/admin/news/${article.id}`)}
                            title="Chỉnh sửa"
                            disabled={!newsPerm.canEdit}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => window.open(`/#/news/${article.id}`, '_blank')}
                            title="Xem trước"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          {newsPerm.canDelete && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(article.id)}
                              title="Xóa"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTable>
          )}
        </div>
      </div>
      )}
    </AdLayout>
  );
};

export default NewsList;
